import React, { useState } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, IconButton, FormControlLabel, Switch, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HousekeepingService } from '../api/services/housekeepingService';
import { useSelector } from 'react-redux';
import { toast } from '../utils/toast';

export default function TaskTypeFormModal({ open, onClose, onSuccess }) {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        defaultDuration: '',
        colorTag: '#FF5722',
        isActive: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.name || !formData.defaultDuration) {
            toast.error("Please fill all required fields");
            return;
        }

        const duration = parseInt(formData.defaultDuration, 10);
        if (isNaN(duration) || duration <= 0) {
            toast.error("Default duration must be a positive number greater than 0");
            return;
        }

        setIsSubmitting(true);
        try {
            // Strict Schema Clean-up Enforcement
            const payload = {
                name: formData.name,
                description: formData.description,
                defaultDuration: duration,
                colorTag: formData.colorTag,
                isActive: formData.isActive
            };

            await HousekeepingService.createTaskType(payload, tenantId);
            toast.success("New housekeeping task type dictionary entry added successfully");
            
            // Clean out local state hooks
            setFormData({
                name: '',
                description: '',
                defaultDuration: '',
                colorTag: '#FF5722',
                isActive: true
            });
            
            // Trigger explicit re-fetch and close
            if (onSuccess) onSuccess();
            onClose();
            
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "Failed to create task type");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={isSubmitting ? undefined : onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 450 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={isSubmitting}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" mb={3}>
                    Create Task Type
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Name *" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleChange} 
                            size="small"
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Description" 
                            name="description" 
                            multiline 
                            rows={3} 
                            value={formData.description} 
                            onChange={handleChange} 
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField 
                            fullWidth 
                            label="Default Duration (mins) *" 
                            type="number"
                            name="defaultDuration" 
                            value={formData.defaultDuration} 
                            onChange={handleChange} 
                            size="small"
                            inputProps={{ min: 1 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Color Tag"
                            type="color"
                            name="colorTag"
                            value={formData.colorTag}
                            onChange={handleChange}
                            size="small"
                            sx={{ '& .MuiInputBase-input': { p: 0.5, height: '23px' } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" color="primary" />}
                            label="Active Status"
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button fullWidth variant="outlined" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
                    >
                        {isSubmitting ? 'Saving...' : 'Save Task Type'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
